import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../utils/axiosConfig";

// Thunks
// Alternar visibilidad (POST, no PATCH — LiteSpeed)
export const toggleEstadoDocumento = createAsyncThunk(
  "documentos/toggleEstado",
  async (id, { rejectWithValue }) => {
    console.log(
      "%c[TOGGLE] 1. Thunk iniciado. id=",
      "color:#d0021b;font-weight:bold",
      id,
    );
    try {
      const { data } = await api.post(`/api/documentos/${id}/estado`);
      console.log(
        "%c[TOGGLE] 2. Respuesta backend cruda:",
        "color:#198754",
        data,
      );
      console.log("[TOGGLE] 3. data.documento =", data.documento);
      if (!data.documento) {
        console.warn(
          "[TOGGLE] ⚠️ data.documento es undefined. El backend no devolvió 'documento'. Revisa la forma de la respuesta.",
        );
      }
      return data.documento; // { id, estado_activo }
    } catch (e) {
      console.error(
        "[TOGGLE] ❌ Error en el POST:",
        e.response?.status,
        e.response?.data,
      );
      return rejectWithValue(
        e.response?.data?.error || "No se pudo cambiar el estado.",
      );
    }
  },
);

// Reemplazar el archivo físico (swap)
export const reemplazarArchivoDocumento = createAsyncThunk(
  "documentos/reemplazarArchivo",
  async ({ id, file }, { rejectWithValue }) => {
    try {
      const fd = new FormData();
      fd.append("archivo", file); // el backend espera el campo "archivo"
      const { data } = await api.post(`/api/documentos/${id}/reemplazar`, fd);
      return data.documento;
    } catch (e) {
      return rejectWithValue(
        e.response?.data?.error || "No se pudo reemplazar el archivo.",
      );
    }
  },
);

export const fetchDocumentos = createAsyncThunk(
  "documentos/fetchDocumentos",
  async (admin = false, { rejectWithValue }) => {
    try {
      const params = admin
        ? `?admin=true&_t=${Date.now()}`
        : `?_t=${Date.now()}`;
      const response = await api.get(`/api/documentos/listar${params}`);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || "Error al obtener documentos",
        status: error.response?.status || 500,
      });
    }
  },
);

export const uploadDocumento = createAsyncThunk(
  "documentos/uploadDocumento",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post("/api/documentos/subir", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      return response.data;
    } catch (error) {
      console.error("[DOCUMENTOS] Error en uploadDocumento:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: error.config,
      });

      if (error.response?.status === 400) {
        const extensions = [
          ".docx",
          ".doc",
          ".docm",
          ".dotx",
          ".dot",
          ".ocx",
          ".pdf",
          ".xlsx",
          ".xls",
        ];
        const defaultMsg = `Formato no válido. Use: ${extensions.join(", ")}`;

        return rejectWithValue({
          message: error.response.data.message || defaultMsg,
          errors: error.response.data.errors || [],
          status: 400,
        });
      }
      return rejectWithValue(
        error.response?.data || "Error al subir documento",
      );
    }
  },
);

export const downloadDocumento = createAsyncThunk(
  "documentos/downloadDocumento",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/documentos/descargar/${id}`, {
        responseType: "blob",
      });

      if (!response.data || response.data.size === 0) {
        console.error(
          "[DOCUMENTOS] Error: Documento vacío recibido para descarga",
        );
        throw new Error("El documento recibido está vacío");
      }

      const contentDisposition = response.headers["content-disposition"];
      const contentType = response.headers["content-type"];

      // Obtener el nombre de archivo original
      let fileName = `documento_${id}`;
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(
          /filename="?(.+?)"?(;|$)/i,
        );
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1].trim();
        }
      }

      // Crear el blob con el tipo MIME exacto recibido del servidor
      const blob = new Blob([response.data], {
        type: contentType || "application/octet-stream",
      });
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", fileName); // Usar el nombre de archivo exacto
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);

      return { id, fileName };
    } catch (error) {
      console.error("[DOCUMENTOS] Error en downloadDocumento:", {
        message: error.message,
        stack: error.stack,
        response: error.response,
        config: error.config,
      });
      return rejectWithValue({
        message:
          error.response?.data?.message || "Error al descargar documento",
        details: error.message,
        status: error.response?.status,
      });
    }
  },
);

export const viewDocumento = createAsyncThunk(
  "documentos/viewDocumento",
  async (id, { rejectWithValue, getState }) => {
    try {
      const token = getState().auth.token;

      const response = await api.get(`/api/documentos/ver/${id}`, {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.data || response.data.size === 0) {
        console.error(
          "[DOCUMENTOS] Error: Documento vacío recibido para visualización",
        );
        throw new Error("El documento recibido está vacío");
      }

      const contentType = response.headers["content-type"];

      let blobType = contentType;

      // Manejo especial para Excel
      if (
        contentType.includes("excel") ||
        contentType.includes("spreadsheet") ||
        contentType.includes("octet-stream") ||
        response.config.url.includes(".xls")
      ) {
        blobType =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      }

      const blobUrl = URL.createObjectURL(
        new Blob([response.data], { type: blobType }),
      );

      const fileName =
        response.headers["content-disposition"]?.match(
          /filename="?(.+?)"?(;|$)/,
        )?.[1] || `documento_${id}.xlsx`;

      return {
        id,
        blobUrl,
        type: blobType,
        url: response.config.url,
        fileName,
        size: response.data.size,
      };
    } catch (error) {
      console.error("[DOCUMENTOS] Error en viewDocumento:", {
        message: error.message,
        stack: error.stack,
        response: error.response,
        config: error.config,
        timestamp: new Date().toISOString(),
      });

      return rejectWithValue({
        message: "Error al cargar el documento",
        details: error.message,
        status: error.response?.status,
        originalError:
          process.env.NODE_ENV === "development" ? error : undefined,
      });
    }
  },
);

export const deleteDocumento = createAsyncThunk(
  "documentos/deleteDocumento",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/api/documentos/eliminar/${id}`);

      return id;
    } catch (error) {
      console.error("[DOCUMENTOS] Error en deleteDocumento:", {
        message: error.message,
        stack: error.stack,
        response: error.response,
        config: error.config,
      });
      return rejectWithValue({
        message: error.response?.data?.message || "Error al eliminar documento",
        details: error.message,
        status: error.response?.status,
      });
    }
  },
);

export const viewPDF = createAsyncThunk(
  "documentos/viewPDF",
  async (url, { getState }) => {
    const token = getState().auth.token;
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  },
);

export const editarDocumento = createAsyncThunk(
  "documentos/editar",
  async ({ id, titulo, descripcion }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/api/documentos/${id}/editar`, {
        titulo,
        descripcion,
      });
      return data.documento;
    } catch (e) {
      return rejectWithValue(
        e.response?.data?.error || "No se pudo editar el documento.",
      );
    }
  },
);

const documentosSlice = createSlice({
  name: "documentos",
  initialState: {
    documentos: [],
    documentoSeleccionado: null,
    loading: false,
    error: null,
    uploadError: null,
    viewStatus: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
    lastAction: null,
    lastActionTimestamp: null,
  },
  reducers: {
    clearDocumentoSeleccionado: (state) => {
      if (state.documentoSeleccionado?.blobUrl) {
        URL.revokeObjectURL(state.documentoSeleccionado.blobUrl);
      }
      state.documentoSeleccionado = null;
    },
    clearUploadError: (state) => {
      state.uploadError = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetViewStatus: (state) => {
      state.viewStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      // Obtener documentos
      .addCase(fetchDocumentos.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.lastAction = "fetchDocumentos";
        state.lastActionTimestamp = new Date().toISOString();
      })
      .addCase(fetchDocumentos.fulfilled, (state, action) => {
        state.loading = false;
        state.documentos = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchDocumentos.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Subir documento
      .addCase(uploadDocumento.pending, (state) => {
        state.loading = true;
        state.uploadError = null;
        state.error = null;
        state.lastAction = "uploadDocumento";
        state.lastActionTimestamp = new Date().toISOString();
      })
      .addCase(uploadDocumento.fulfilled, (state, action) => {
        state.loading = false;
        state.documentos.unshift(action.payload.documento);
      })
      .addCase(uploadDocumento.rejected, (state, action) => {
        console.error(
          "[DOCUMENTOS] uploadDocumento.rejected - Error:",
          action.payload,
        );
        state.loading = false;
        state.uploadError = action.payload;
      })
      .addCase(viewPDF.fulfilled, (state, action) => {
        state.pdfViewerUrl = action.payload;
      })

      // Descargar documento
      .addCase(downloadDocumento.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.lastAction = "downloadDocumento";
        state.lastActionTimestamp = new Date().toISOString();
      })
      .addCase(downloadDocumento.fulfilled, (state, action) => {
        state.loading = false;
        state.documentoSeleccionado = { id: action.payload.id };
      })
      .addCase(downloadDocumento.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Visualizar documento
      .addCase(viewDocumento.pending, (state) => {
        state.viewStatus = "loading";
        state.error = null;
        state.lastAction = "viewDocumento";
        state.lastActionTimestamp = new Date().toISOString();
      })
      .addCase(viewDocumento.fulfilled, (state, action) => {
        state.viewStatus = "succeeded";
        state.documentoSeleccionado = {
          id: action.payload.id,
          blobUrl: action.payload.blobUrl,
          type: action.payload.type,
          fileName: action.payload.fileName,
          size: action.payload.size,
        };
      })
      .addCase(viewDocumento.rejected, (state, action) => {
        console.error(
          "[DOCUMENTOS] viewDocumento.rejected - Error:",
          action.payload,
        );
        state.viewStatus = "failed";
        state.error = {
          ...action.payload,
          timestamp: new Date().toISOString(),
        };
      })
      .addCase(toggleEstadoDocumento.fulfilled, (state, action) => {
        console.log(
          "%c[REDUCER] toggle.fulfilled. payload=",
          "color:#0d6efd",
          action.payload,
        );
        const i = state.documentos.findIndex(
          (d) => d.id === action.payload?.id,
        );
        console.log(
          "[REDUCER] índice encontrado:",
          i,
          "| id buscado:",
          action.payload?.id,
          "| ids en store:",
          state.documentos.map((d) => d.id),
        );
        if (i !== -1) {
          state.documentos[i].estado_activo = action.payload.estado_activo;
          console.log(
            "[REDUCER] ✅ estado_activo actualizado a:",
            action.payload.estado_activo,
          );
        } else {
          console.warn(
            "[REDUCER] ⚠️ No se encontró el documento en el store. El badge NO cambiará.",
          );
        }
      })
      .addCase(reemplazarArchivoDocumento.fulfilled, (state, action) => {
        const i = state.documentos.findIndex((d) => d.id === action.payload.id);
        if (i !== -1) {
          state.documentos[i] = {
            ...state.documentos[i],
            ...action.payload,
          };
        }
      })

      // Eliminar documento
      .addCase(deleteDocumento.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.lastAction = "deleteDocumento";
        state.lastActionTimestamp = new Date().toISOString();
      })
      .addCase(deleteDocumento.fulfilled, (state, action) => {
        state.loading = false;
        state.documentos = state.documentos.filter(
          (doc) => doc.id !== action.payload,
        );
      })
      .addCase(deleteDocumento.rejected, (state, action) => {
        console.error(
          "[DOCUMENTOS] deleteDocumento.rejected - Error:",
          action.payload,
        );
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(editarDocumento.fulfilled, (state, action) => {
        const i = state.documentos.findIndex((d) => d.id === action.payload.id);
        if (i !== -1)
          state.documentos[i] = { ...state.documentos[i], ...action.payload };
      });
  },
});

export const {
  clearDocumentoSeleccionado,
  clearUploadError,
  clearError,
  resetViewStatus,
} = documentosSlice.actions;

export default documentosSlice.reducer;
